import { useMemo } from 'react';
import { useOrganizationNamesQuery } from '@/graphql/hooks/organizations-hook';
import { roleDefinitions } from '../roleDefinitions';
import { AvailableRoles } from '../types';

export interface OrganizationWithCourses {
  id: string;
  name: string;
  courses: {
    id: string;
    name: string;
  }[];
}

/**
 * Hook to get available roles and organizations/courses based on the current scope
 */
export function useAvailableRoles(organizationId?: string, courseId?: string) {
  const [{ data: orgsData }] = useOrganizationNamesQuery();

  // Get filtered organizations and courses based on current scope
  const [namesById, orgs] = useMemo(() => {
    if (orgsData?.jrnyOrganizations) {
      const orgs = orgsData.jrnyOrganizations
        .filter(org => {
          if (organizationId && org.id !== organizationId) {
            return false;
          }
          return true;
        })
        .map(org => ({
          id: org.id,
          name: org.name,
          courses: org.courses
            .filter(course => {
              if (courseId && course.id !== courseId) {
                return false;
              }
              return true;
            })
            .map(course => ({
              id: course.id,
              name: course.name,
            })),
        }));

      // Create a mapping of IDs to names for easy lookup
      const namesById = orgs.reduce(
        (acc, org) => {
          acc[org.id] = org.name;
          org.courses.forEach(course => {
            acc[course.id] = course.name;
          });
          return acc;
        },
        {} as Record<string, string>
      );

      return [namesById, orgs] as const;
    }
    return [{}, [] as OrganizationWithCourses[]] as const;
  }, [orgsData?.jrnyOrganizations, organizationId, courseId]);

  // Get allowed roles based on the current scope
  const availableRoleTypes = useMemo(() => {
    const allRoles = Object.keys(roleDefinitions) as AvailableRoles[];
    
    return allRoles.filter(r => {
      if (organizationId && roleDefinitions[r].scope === 'global') {
        return false;
      }
      if (courseId && (roleDefinitions[r].scope === 'global' || roleDefinitions[r].scope === 'organization')) {
        return false;
      }
      return true;
    });
  }, [organizationId, courseId]);

  return {
    namesById,
    organizations: orgs,
    availableRoleTypes,
    roleDefinitions
  };
} 